import * as I from 'immutable';
import React from 'react';

import ConnectedMap from 'app/components/ConnectedMap';
import {RenderMapCallbackOpts} from 'app/components/ConnectedMap/view';
import DraggableLegend from 'app/components/DeclarativeMap/DraggableLegend';
import FeatureCollectionVector from 'app/components/DeclarativeMap/FeatureCollectionVector';
import FeatureRaster from 'app/components/DeclarativeMap/FeatureRaster';
import GeoJsonFitter from 'app/components/DeclarativeMap/GeoJsonFitter';
import GeometryOverlaysContent from 'app/components/DeclarativeMap/GeometryOverlaysContent';
import MapSettingsControl from 'app/components/DeclarativeMap/MapSettingsControl';
import MapStyle from 'app/components/DeclarativeMap/MapStyle';
import MapTilesLoadingControl from 'app/components/DeclarativeMap/MapTilesLoadingControl';
import NoteVector from 'app/components/DeclarativeMap/NoteVector';
import {ApiFeature, ApiFeatureData} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection, GeometryOverlaySetting} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {MapInteractionProvider} from 'app/pages/MonitorProjectView/Map';
import {MapSettingsTool} from 'app/pages/MonitorProjectView/view';
import {GeoJsonFeaturesLoader} from 'app/providers/FeaturesProvider';
import {DEFAULT_NOTES_STATE, NotesState} from 'app/stores/NotesStore';
import * as featureCollectionUtils from 'app/utils/featureCollectionUtils';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import * as layers from 'app/utils/layers';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';

import {StorySlide} from './storySlides';

const EMPTY_FEATURE_DATA = I.List() as unknown as I.ImmutableOf<ApiFeatureData[]>;
const CAMERA_DURATION_MS = 900;

// Same caps the monitoring map uses when it zooms to a note. A point note has
// no extent of its own, so this is what decides how close the camera gets.
function maxFitZoom(layerKey: string) {
  return layerUtils.isLayerKeyHighResTruecolor(layerKey) || layerKey === layers.NONE ? 18 : 15;
}

// Stable no-ops: fresh closures would defeat ConnectedMap's PureComponent bail-out
// and re-register map listeners on every render.
const NOOP = () => {};

interface Props {
  slide: StorySlide;
  /** The slide's refs after reconciling with featureData. */
  imageRefs: mapUtils.MapImageRefs;
  featureData: I.ImmutableOf<ApiFeatureData[]> | null;
  /** Every property the story touches, so outlines stay put while slides change. */
  features: I.ImmutableOf<ApiFeature[]>;
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  overlayFeatureCollections: I.ImmutableOf<ApiFeatureCollection[]>;
  overlaySettings: GeometryOverlaySetting[];
  isOverlayVisibleByName: Record<string, boolean>;
  geoJsonFeaturesLoader: GeoJsonFeaturesLoader;
  organization: I.ImmutableOf<ApiOrganization>;
  firebaseToken: string;
  /** Keeps the framed geometry clear of the caption card and bar. */
  fitPadding: mapboxgl.PaddingOptions;
  /** False until the padding reflects this slide's caption, so the fit lands once, correctly. */
  isCameraReady: boolean;
  mapStyle: mapUtils.MapStyle;
  activeTool: MapSettingsTool | null;
  setActiveTool: (tool: MapSettingsTool | null) => void;
}

const StoryMap: React.FunctionComponent<Props> = ({
  slide,
  imageRefs,
  featureData,
  features,
  featureCollection,
  overlayFeatureCollections,
  overlaySettings,
  isOverlayVisibleByName,
  geoJsonFeaturesLoader,
  organization,
  firebaseToken,
  fitPadding,
  isCameraReady,
  mapStyle,
  activeTool,
  setActiveTool,
}) => {
  const hydratedFeatureCollection = React.useMemo(
    () => featureCollectionUtils.hydratedFeatureCollection(featureCollection, features),
    [featureCollection, features]
  );
  const selectedFeatures = React.useMemo(() => features.toSet(), [features]);
  const selectedFeatureIds = React.useMemo(
    () => I.Set([slide.feature.get('id') as number]),
    [slide.feature]
  );

  const note = slide.kind === 'note' ? slide.note : null;
  const notesState = React.useMemo<NotesState>(
    () =>
      note ? {...DEFAULT_NOTES_STATE, notes: [note], focusedNoteId: note.id} : DEFAULT_NOTES_STATE,
    [note]
  );

  const fitGeoJson = slide.mapBounds ? geoJsonUtils.bboxPolygon(slide.mapBounds) : slide.geometry;
  const maxZoom = maxFitZoom(imageRefs[0].layerKey);
  const fitBoundsOptions = React.useMemo<mapboxgl.EasingOptions>(
    () => ({padding: fitPadding, maxZoom, duration: CAMERA_DURATION_MS}),
    [fitPadding, maxZoom]
  );

  return (
    <ConnectedMap
      imageRefs={imageRefs}
      featureData={featureData}
      mode={imageRefs.length === 2 ? mapUtils.MODES.COMPARE : mapUtils.MODES.VIEW}
      selectedFeatures={selectedFeatures}
      selectedFeatureCollection={hydratedFeatureCollection}
      selectedFeatureIds={selectedFeatureIds}
      organization={organization}
      firebaseToken={firebaseToken}
      onMapClick={NOOP}
    >
      {({map, isMapLoaded, cursor, layerKey, index, isCameraLeader}: RenderMapCallbackOpts) => (
        <MapInteractionProvider
          features={features}
          selectedFeatureIds={selectedFeatureIds}
          setSelectedFeatureIds={NOOP}
        >
          {({selectFeatureById, hoveredFeatureIds, hoverFeatureById}) => (
            <>
              <mapUtils.DisableClickableFeatures />
              <FeatureRaster
                map={map}
                isMapLoaded={isMapLoaded}
                featureData={featureData ?? EMPTY_FEATURE_DATA}
                activeLayerKey={layerKey}
                cursor={cursor}
                holdPreviousUntilLoaded
              />
              <FeatureCollectionVector
                map={map}
                isMapLoaded={isMapLoaded}
                featureCollection={hydratedFeatureCollection}
                selectedFeatureIds={selectedFeatureIds}
                selectFeatureById={selectFeatureById}
                hoveredFeatureIds={hoveredFeatureIds}
                hoverFeatureById={hoverFeatureById}
                showPropertyBoundaries
                mode="focus"
              />
              {overlayFeatureCollections.size > 0 && (
                <GeometryOverlaysContent
                  map={map}
                  isMapLoaded={isMapLoaded}
                  selectedFeatures={selectedFeatures}
                  overlayFeatureCollections={overlayFeatureCollections}
                  geoJsonFeaturesLoader={geoJsonFeaturesLoader}
                  isOverlayVisibleByName={isOverlayVisibleByName}
                  overlaySettings={overlaySettings}
                  ignoreClickableFeaturesDisabled
                />
              )}
              {note && (
                <NoteVector
                  map={map}
                  isMapLoaded={isMapLoaded}
                  notesState={notesState}
                  notesActions={null}
                  isVisible
                />
              )}
              {/* Camera leader only; the compare before-map follows via CompareMap's sync.
                  Keyed per slide so each one gets its own fit, even when two slides
                  frame the same geometry and the viewer has panned away. */}
              {isCameraLeader && (
                <GeoJsonFitter
                  key={slide.key}
                  map={map}
                  geoJson={isCameraReady ? fitGeoJson : null}
                  fitBoundsOptions={fitBoundsOptions}
                />
              )}
              <MapSettingsControl
                map={map}
                isMapLoaded={isMapLoaded}
                position="top-right"
                activeMapSettingsTool={activeTool}
                setActiveMapSettingsTool={setActiveTool}
              />
              <DraggableLegend
                imageRefs={imageRefs}
                layer={layerUtils.getLayer(layerKey)}
                index={index}
              />
              <MapTilesLoadingControl map={map} isMapLoaded={isMapLoaded} />
              <MapStyle map={map} style={mapStyle} />
            </>
          )}
        </MapInteractionProvider>
      )}
    </ConnectedMap>
  );
};

export default React.memo(StoryMap);
